import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { patchAssetCaches } from "@/api/asset-cache";
import { confirmImageUse } from "../lib/confirmImageUse";
import type { V2Asset } from "../types";
import { EditorImageRightsDialog } from "./EditorImageRightsDialog";

/** Keep unconfirmed uploads out of quote requests; ask only when Generate is pressed. */
export function useImageUseConfirmation(inputs: readonly (V2Asset | undefined)[]) {
  const client = useQueryClient();
  const [confirmed, setConfirmed] = useState<Record<string, V2Asset>>({});
  const [open, setOpen] = useState(false);
  const assets = inputs.map(asset => {
    if (!asset) return undefined;
    const saved = confirmed[asset.id];
    return saved && saved.space_id === asset.space_id && saved.version >= asset.version && asset.rights_status !== "revoked" ? saved : asset;
  });
  const pending = [...new Map(assets.filter((asset): asset is V2Asset => asset?.rights_status === "pending").map(asset => [asset.id, asset])).values()];
  const revoked = assets.some(asset => asset?.rights_status === "revoked");
  const needsConfirmation = pending.length > 0 && !revoked;
  return {
    assets, needsConfirmation, ready: !pending.length && !revoked,
    requestConfirmation: () => setOpen(true),
    dialog: open && pending.length > 0 && <EditorImageRightsDialog assets={pending} onClose={() => setOpen(false)} onConfirm={async selected => {
      // Retain each successful confirmation if a later request needs retrying.
      for (const asset of selected) {
        const next = await confirmImageUse(asset);
        setConfirmed(old => ({ ...old, [next.id]: next }));
        patchAssetCaches(client, next.space_id, new Map([[next.id, next]]));
      }
    }} />,
  };
}
